import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import NonScriptRangeBundles from '../components/NonScriptRangeBundles';
import Tabs from '../components/Tabs';
import showToast from '../components/ToastComponent';
import { TabItem } from '../interface/interface';
import { addProductToCart } from '../service/addProductToCart';
import { removeProductFromCart } from '../service/removeProductFromCart';
import { getCart } from '../service/getCart';
import { NonScriptRangeProducts } from '../components/NonScriptRangeProducts';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../context/userContext';
import { CartContext } from '../context/cartContext';
import { WishListContext } from '../context/wishListContext';
import RadionButton from '../components/RadioButton';
import Breadcrumbs from '../components/Breadcrumbs';
import { useLocation } from 'react-router-dom';

export default function NonScriptRange() {
  const {
    setLoadingEnable,
    setHideDropdown,
    setIsMobileDropdownOpen,
    setActiveItem,
    isMobile,
  } = useContext(UserContext);
  const { setCart } = useContext(CartContext);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { wishListData } = useContext(WishListContext);
  const [selectedRowIndex, setSelectedRowIndex] = useState<string>('1');
  const location = useLocation();

  const handleRadioButtonChange = (value: string) => {
    setSelectedRowIndex(value);
  };

  const { isLoading } = useQuery({
    queryKey: ['cart', user?.access_token],
    queryFn: async () => {
      const result = await getCart(user!.access_token);
      if (result === undefined) {
        showToast({
          message: 'Unable to fetch existing cart!',
          type: 'error',
        });
        return null;
      }

      setCart(result);
      return result;
    },
    enabled: !!user?.access_token,
    refetchOnMount: false,
  });

  useEffect(() => {
    setHideDropdown(true);
    setIsMobileDropdownOpen(false);
    setActiveItem('Shop non-script');
  }, []);

  const addToCartMutation = useMutation({
    mutationFn: async (productId: string) => {
      const result = await addProductToCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item added to your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }

      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
      return null;
    },
    mutationKey: ['addToCart', user?.access_token],
  });

  const removeProductFromCartMutation = useMutation({
    mutationKey: ['removeCartItem', user?.access_token],
    mutationFn: async (productId: string) => {
      const result = await removeProductFromCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }
      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
    },
  });

  useEffect(() => {
    if (
      !isLoading ||
      !addToCartMutation.isPending ||
      !removeProductFromCartMutation.isPending
    ) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [
    isLoading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  const tabs: Array<TabItem> = [
    {
      id: '1',
      label: 'Products',
      component: (
        <NonScriptRangeProducts
          addToCartMutation={addToCartMutation}
          removeFromCartMutation={removeProductFromCartMutation}
          loading={isLoading}
          wishListProducts={wishListData}
        />
      ),
    },
    {
      id: '2',
      label: 'Bundles',
      component: (
        <NonScriptRangeBundles
          addToCartMutation={addToCartMutation}
          removeFromCartMutation={removeProductFromCartMutation}
          loading={isLoading}
          wishListProducts={wishListData}
        />
      ),
    },
  ];

  return (
    <div className="container-standard">
      {isMobile ? (
        <Tabs tabs={tabs} />
      ) : (
        <>
          {location?.state?.needBreadcrumbs ? (
            <Breadcrumbs
              data={[
                {
                  tag: 'a',
                  href: '/home',
                  children: <>Home</>,
                },
                {
                  tag: 'p',
                  children: <span>Non Script Range</span>,
                },
              ]}
            />
          ) : (
            <></>
          )}
          <div className="flex items-center md:gap-4 gap-2 mb-6">
            <p className="text-primary-blue-500">Shop by :</p>

            <RadionButton
              selectedRowIndex={selectedRowIndex === '1'}
              handleRadioButtonChange={() => handleRadioButtonChange('1')}
              name={'Products'}
            />

            <RadionButton
              selectedRowIndex={selectedRowIndex === '2'}
              handleRadioButtonChange={() => handleRadioButtonChange('2')}
              name={'Bundles'}
            />
          </div>

          {selectedRowIndex === '1' && (
            <NonScriptRangeProducts
              addToCartMutation={addToCartMutation}
              removeFromCartMutation={removeProductFromCartMutation}
              loading={isLoading}
              wishListProducts={wishListData}
            />
          )}

          {selectedRowIndex === '2' && (
            <NonScriptRangeBundles
              addToCartMutation={addToCartMutation}
              removeFromCartMutation={removeProductFromCartMutation}
              loading={isLoading}
              wishListProducts={wishListData}
            />
          )}
        </>
      )}
    </div>
  );
}
